import { ReactNode, useCallback, useMemo } from 'react';
import { Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { BracesVariableRegular, ShieldPersonRegular, Info20Regular, SettingsCogMultiple20Regular } from '@fluentui/react-icons';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth';
import { PermissionBanner } from '@/components/shared/PermissionBanner';
import { useCheckAccess, checkUserAccess } from '@/hooks/useUserPermissions';
import { useAppPermission } from '@/hooks/useAppPermission';
import { APPLICATION_PERMISSIONS } from '@/types/permissions';
import { ApplicationStatusTab, EnvironmentVariablesTab, ConfigurationTab } from '@/components/Settings';
import { Permissions } from './Permissions/Permissions';

export interface SettingsDialogTab {
  /** Unique identifier for the tab */
  value: string;
  /** Display label for the tab */
  label: string;
  /** Optional icon for the tab */
  icon?: JSX.Element;
  /** Content to render when tab is selected. Receives mountNode for rendering drawers within the dialog. */
  content: (mountNode: HTMLElement | null) => ReactNode;
}

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: tokens.spacingVerticalXXXL,
  },
  errorText: {
    color: tokens.colorStatusDangerForeground1,
  },
});

export interface UseApplicationConfigTabsOptions {
  projectName: string;
  appName: string;
  /**
   * Additional content rendered after the Permissions panel inside the
   * permissions tab (e.g. the page variant's "Danger zone" delete affordance).
   */
  permissionsExtraContent?: React.ReactNode;
}

export interface UseApplicationConfigTabsResult {
  /** Tabs in order: general, env-vars, configuration, permissions. */
  tabs: SettingsDialogTab[];
  generalTab: SettingsDialogTab;
  envVarsTab: SettingsDialogTab;
  configurationTab: SettingsDialogTab;
  permissionsTab: SettingsDialogTab;
}

/**
 * Shared tab definitions for application-level configuration. Backs both
 * `ApplicationSettings` (modal) and `ConfigurationPage` (route) so the two
 * surfaces expose the same content with the same data flow.
 */
export function useApplicationConfigTabs({
  projectName,
  appName,
  permissionsExtraContent,
}: UseApplicationConfigTabsOptions): UseApplicationConfigTabsResult {
  const styles = useStyles();
  const { account, getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const { canEdit, permission, isLoading: isAppPermissionLoading } = useAppPermission();
  const { data: myAccess, isLoading, isError } = useCheckAccess(projectName, appName, null);
  const currentUserAccess = useMemo(() => {
    if (isLoading || isAppPermissionLoading) return null;
    return permission === 'Owner'
      ? { role: 'Owner', permissions: [{
          id: 'Owner',
          name: 'Owner',
          description: 'Full access to all application resources and permissions. Can delete application.',
      }] }
      : myAccess;
  }, [isLoading, isAppPermissionLoading, permission, myAccess]);

  const resourceDetails = useMemo(() => ({ projectName, appName }), [projectName, appName]);

  const checkAccess = useCallback(
    async (userId: string) => {
      try {
        return await checkUserAccess(
          queryClient,
          account?.tenantId ?? '',
          userId,
          projectName,
          appName,
          getOttoToken,
        );
      } catch (error) {
        console.error('Error checking access for user', userId, error);
        throw error;
      }
    },
    [queryClient, account?.tenantId, projectName, appName, getOttoToken],
  );

  const generalTab: SettingsDialogTab = useMemo(
    () => ({
      value: 'general',
      label: 'General',
      icon: <Info20Regular />,
      content: () => <ApplicationStatusTab projectName={projectName} appName={appName} />,
    }),
    [projectName, appName],
  );

  const envVarsTab: SettingsDialogTab = useMemo(
    () => ({
      value: 'env-vars',
      label: 'Environment variables',
      icon: <BracesVariableRegular />,
      content: () => {
        return canEdit
          ? <EnvironmentVariablesTab projectName={projectName} appName={appName} />
          : <div>
              <PermissionBanner
                title="Insufficient permissions"
                message="You don't have the correct permissions to view application environment variables. Contact the application administrator to request Contributor access."
              />
          </div>;
      },
    }),
    [projectName, appName, canEdit],
  );

  const configurationTab: SettingsDialogTab = useMemo(
    () => ({
      value: 'configuration',
      label: 'Configuration',
      icon: <SettingsCogMultiple20Regular />,
      content: () => <ConfigurationTab projectName={projectName} appName={appName} />,
    }),
    [projectName, appName],
  );

  const permissionsTab: SettingsDialogTab = useMemo(() => {
    if (isLoading || isAppPermissionLoading) {
      return {
        value: 'permissions',
        label: 'User permissions',
        icon: <ShieldPersonRegular />,
        content: () => (
          <div className={styles.loadingContainer}>
            <Spinner size="medium" label="Loading permissions..." />
          </div>
        ),
      };
    }
    if (isError || !currentUserAccess) {
      return {
        value: 'permissions',
        label: 'User permissions',
        icon: <ShieldPersonRegular />,
        content: () => (
          <div>
            <Text className={styles.errorText}>Failed to load permissions. Please try again.</Text>
          </div>
        ),
      };
    }
    return {
      value: 'permissions',
      label: 'User permissions',
      icon: <ShieldPersonRegular />,
      content: (mountNode: HTMLElement | null) => (
        <>
          <Permissions
            mountNode={mountNode}
            supportedPermissions={APPLICATION_PERMISSIONS}
            resourceDetails={resourceDetails}
            myAccess={currentUserAccess}
            checkAccess={checkAccess}
            canEdit={canEdit}
          />
          {permissionsExtraContent}
        </>
      ),
    };
  }, [isLoading, isAppPermissionLoading, isError, currentUserAccess, styles.loadingContainer, styles.errorText, resourceDetails, checkAccess, canEdit, permissionsExtraContent]);

  const tabs = useMemo(
    () => [generalTab, envVarsTab, configurationTab, permissionsTab],
    [generalTab, envVarsTab, configurationTab, permissionsTab],
  );

  return { tabs, generalTab, envVarsTab, configurationTab, permissionsTab };
}
